<script lang="ts" generic="T">
export default {
  name: 'KkItemList',
}
</script>

<script lang="ts" setup>
import { computed } from 'vue'

export type ItemListHeader = {
  key: string
  title?: string
  width?: string
}

export interface Props {
  header: ItemListHeader[]
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  items: T[]
}

export interface Emits {
  (key: 'sort', value: string): void
}

const props = defineProps<Props>()

const colWidths = computed(() => {
  if (!props.header.some((head) => head.width)) return []
  return props.header.map((head) => head.width || 'auto')
})

</script>

<template>
  <div>
    <table :class="$style.table">
      <colgroup v-if="colWidths.length">
        <col v-for="(width, i) in colWidths" :key="i" :style="{ width }">
      </colgroup>

      <thead :class="$style.header">
        <tr>
          <template v-for="head in props.header" :key="head.key">
            <component :is="head.title ? 'th' : 'td'" :class="$style.head">{{ head.title }}</component>
          </template>
        </tr>
      </thead>

      <tbody :class="$style.body">
        <tr v-for="item in props.items" :key="item.id" class="item">
          <slot name="item" v-bind="{ item }" />
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" module>
@import "../../../../../../stylesheets/biz_spa/foundation/var";
@import "../../../../../../stylesheets/biz_spa/foundation/mixin";
.table {
  display: block;
  @include mq(sm) {
    display: table;
    width: 100%;
    margin: -8px 0;
    border-spacing: 0 8px;
  }
}
.header {
  display: none;
  @include mq(sm) {
    display: table-header-group;
  }
}
.head {
  padding: 13px 16px 12px;
  background-color: #ECEDF0;
  font-size: 14px;
  text-align: left;
  &:first-child {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }
  &:last-child {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }
}
.body {
  display: block;
  @include mq(sm) {
    display: table-row-group;
  }
}
</style>

<style lang="scss" scoped>
@import "../../../../../../stylesheets/biz_spa/foundation/var";
@import "../../../../../../stylesheets/biz_spa/foundation/mixin";
.item {
  display: block;
  padding: 16px;
  border: 1px solid #E4E7EB;
  border-radius: 4px;
  &:not(:first-child) {
    margin-top: 16px;
  }
  @include mq(sm) {
    display: table-row;
  }
  /* stylelint-disable selector-pseudo-class-no-unknown */
  :deep(td) {
    display: block;
    &::before {
      color: #777;
      font-weight: normal;
      content: attr(data-label);
    }
    @include mq(sm) {
      display: table-cell;
      padding: 15px 16px 14px;
      font-size: 14px;
      border-top: 1px solid #E4E7EB;
      border-bottom: 1px solid #E4E7EB;
      &::before {
        display: none;
      }
      &:first-child {
        border-top-left-radius: 4px;
        border-bottom-left-radius: 4px;
        border-left: 1px solid #E4E7EB;
      }
      &:last-child {
        border-top-right-radius: 4px;
        border-bottom-right-radius: 4px;
        border-right: 1px solid #E4E7EB;
      }
    }
  }
}
</style>
